import {
  BlockType,
  buildRawBlockString,
  type FilterConfig,
  FilterOperator,
  FilterQueryParser,
  getFilterValue,
  getProviderKey,
  type IndexedAnyBlock,
  type IndexedFilterBlock,
  type IndexedGroupBlock,
  type IndexedKeywordBlock,
  isIndexedAnyBlock,
} from '@github-ui/filter-core'

import {createParserProvider} from './create-parser-provider'
import {cleanConsecutiveSpaces, type TokenType} from './query-values'
import {MAX_QUERY_LENGTH} from './tokenize-query'

const structuralProviderKeys = ['label', 'project', 'author', 'assignee', 'milestone', 'review', 'type', 'no'] as const

const groupingSupportConfig: FilterConfig = {
  filterDelimiter: ':',
  valueDelimiter: ',',
  variant: 'input',
  groupAndKeywordSupport: true,
}

type BlockContainer = {
  blocks: IndexedAnyBlock[]
  group?: IndexedGroupBlock
  parent?: BlockContainer
}

type FilterBranch = {
  block: IndexedFilterBlock
  container: BlockContainer
}

type BranchOperand = {
  block: IndexedFilterBlock | IndexedGroupBlock
  container: BlockContainer
}

function significantBlocks(blocks: IndexedAnyBlock[]) {
  return blocks.filter(block => block.type !== BlockType.Space)
}

function isBooleanKeyword(block: IndexedAnyBlock | undefined): block is IndexedKeywordBlock {
  return block?.type === BlockType.Keyword && (block.raw.toUpperCase() === 'AND' || block.raw.toUpperCase() === 'OR')
}

function hasUnsafeStructure(blocks: IndexedAnyBlock[]): boolean {
  const significant = significantBlocks(blocks)
  if (significant.length === 0) return false

  let expectsOperand = true
  let hasImplicitAnd = false
  const operators = new Set<string>()
  for (const block of significant) {
    if (block.type === BlockType.UnmatchedOpenParen || block.type === BlockType.UnmatchedCloseParen) return true
    if (
      block.type === BlockType.Group &&
      (significantBlocks(block.blocks).length === 0 || hasUnsafeStructure(block.blocks))
    ) {
      return true
    }

    if (isBooleanKeyword(block)) {
      if (expectsOperand) return true
      operators.add(block.raw.toUpperCase())
      expectsOperand = true
    } else {
      if (!expectsOperand) hasImplicitAnd = true
      expectsOperand = false
    }
  }

  return expectsOperand || (operators.has('OR') && (operators.has('AND') || hasImplicitAnd))
}

function isEscapedCharacter(value: string, index: number): boolean {
  let precedingBackslashes = 0
  for (let previousIndex = index - 1; value[previousIndex] === '\\'; previousIndex--) precedingBackslashes++
  return precedingBackslashes % 2 !== 0
}

function hasUnsafeQuote(searchQuery: string): boolean {
  let inQuotes = false

  for (let index = 0; index < searchQuery.length; index++) {
    const character = searchQuery[index]
    if (character === '"') {
      if (isEscapedCharacter(searchQuery, index)) return true
      inQuotes = !inQuotes
    }
  }

  return inQuotes
}

function containsFilterOrBooleanStructure(blocks: IndexedAnyBlock[]): boolean {
  return blocks.some(
    block =>
      block.type === BlockType.Filter ||
      block.type === BlockType.Keyword ||
      (block.type === BlockType.Group && containsFilterOrBooleanStructure(block.blocks)),
  )
}

function maskBalancedFilterValueParentheses(searchQuery: string, parser: FilterQueryParser): string | undefined {
  const maskedQuery = searchQuery.split('')
  let inQuotes = false
  let tokenStart = 0

  const maskToken = (start: number, end: number): boolean => {
    const filterDelimiterIndex = searchQuery.indexOf(groupingSupportConfig.filterDelimiter, start)
    if (filterDelimiterIndex < start || filterDelimiterIndex >= end) return true

    const openParentheses: number[] = []
    const pairs: Array<[number, number]> = []
    let tokenInQuotes = false

    for (let index = filterDelimiterIndex + 1; index < end; index++) {
      const character = searchQuery[index]
      if (character === '"' && !isEscapedCharacter(searchQuery, index)) {
        tokenInQuotes = !tokenInQuotes
      } else if (!tokenInQuotes && character === '(') {
        openParentheses.push(index)
      } else if (!tokenInQuotes && character === ')' && openParentheses.length > 0) {
        const openIndex = openParentheses.pop()
        if (openIndex !== undefined) pairs.push([openIndex, index])
      }
    }

    for (const [openIndex, closeIndex] of pairs) {
      const blocks = parser.parse(searchQuery.slice(openIndex + 1, closeIndex)).blocks
      if (!blocks.every(isIndexedAnyBlock) || containsFilterOrBooleanStructure(blocks)) continue

      maskedQuery[openIndex] = '['
      maskedQuery[closeIndex] = ']'
    }
    return openParentheses.length === 0
  }

  for (let index = 0; index < searchQuery.length; index++) {
    const character = searchQuery[index]
    if (character === '"' && !isEscapedCharacter(searchQuery, index)) inQuotes = !inQuotes
    if (/\s/.test(character ?? '') && !inQuotes && tokenStart < index) {
      if (!maskToken(tokenStart, index)) return undefined
      tokenStart = index + 1
    } else if (/\s/.test(character ?? '') && !inQuotes) {
      tokenStart = index + 1
    }
  }

  if (tokenStart < searchQuery.length && !maskToken(tokenStart, searchQuery.length)) return undefined

  return maskedQuery.join('')
}

function splitFilterValues(rawValues: string): string[] {
  const values: string[] = []
  let currentValue = ''
  let inQuotes = false

  for (let index = 0; index < rawValues.length; index++) {
    const character = rawValues[index] ?? ''
    if (character === '"' && !isEscapedCharacter(rawValues, index)) inQuotes = !inQuotes
    if (character === groupingSupportConfig.valueDelimiter && !inQuotes) {
      values.push(currentValue)
      currentValue = ''
    } else {
      currentValue += character
    }
  }

  values.push(currentValue)
  return values
}

function normalizeFilterValue(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? ''
  return trimmed.startsWith('"') && trimmed.endsWith('"') ? trimmed.slice(1, -1).trim() : trimmed
}

function isSemanticallyEmptyValue(value: string | null | undefined): boolean {
  return normalizeFilterValue(value) === ''
}

function containsEmptyFilter(blocks: IndexedAnyBlock[]): boolean {
  return blocks.some(
    block =>
      (block.type === BlockType.Filter &&
        (block.value.values.length === 0 ||
          block.value.values.some(value => isSemanticallyEmptyValue(getFilterValue(value.value))))) ||
      (block.type === BlockType.Text &&
        block.raw.indexOf(groupingSupportConfig.filterDelimiter) > 0 &&
        splitFilterValues(block.raw.slice(block.raw.indexOf(groupingSupportConfig.filterDelimiter) + 1)).some(
          isSemanticallyEmptyValue,
        )) ||
      (block.type === BlockType.Group && containsEmptyFilter(block.blocks)),
  )
}

function isNoValueBranch(block: IndexedFilterBlock, type: TokenType): boolean {
  return (
    getProviderKey(block.key.value).toLowerCase() === 'no' &&
    block.value.values.length === 1 &&
    getFilterValue(block.value.values[0]?.value)?.toLowerCase() === type
  )
}

function containsAmbiguousNoValueBranch(blocks: IndexedAnyBlock[], type: TokenType): boolean {
  return blocks.some(
    block =>
      (block.type === BlockType.Filter &&
        getProviderKey(block.key.value).toLowerCase() === 'no' &&
        block.value.values.length !== 1 &&
        block.value.values.some(value => normalizeFilterValue(getFilterValue(value.value)).toLowerCase() === type)) ||
      (block.type === BlockType.Group && containsAmbiguousNoValueBranch(block.blocks, type)),
  )
}

function findFilterBranches(blocks: IndexedAnyBlock[], type: TokenType): FilterBranch[] {
  const branches: FilterBranch[] = []
  const root: BlockContainer = {blocks}

  const visit = (container: BlockContainer) => {
    for (const block of container.blocks) {
      if (block.type === BlockType.Group) {
        visit({blocks: block.blocks, group: block, parent: container})
      } else if (
        block.type === BlockType.Filter &&
        (getProviderKey(block.key.value).toLowerCase() === type || isNoValueBranch(block, type))
      ) {
        branches.push({block, container})
      }
    }
  }

  visit(root)
  return branches
}

function normalizeTokens(tokens: string | readonly string[] | undefined, type: TokenType) {
  if (tokens === undefined || tokens === '') return {valid: true, tokens: []}

  const values = typeof tokens === 'string' ? [tokens] : tokens
  if (values.some(value => !value || value.trim() !== value)) return {valid: false, tokens: []}

  const normalized = [...new Map(values.map(value => [value.toLowerCase(), value])).values()]
  const noValue = `no:${type}`
  const noValueIndex = normalized.findIndex(value => value.toLowerCase() === noValue)
  if (noValueIndex !== -1 && normalized.length !== 1) return {valid: false, tokens: []}
  if (noValueIndex !== -1) normalized[noValueIndex] = noValue

  return {valid: true, tokens: normalized}
}

function buildFilterBranch(type: TokenType, tokens: readonly string[]): string {
  if (tokens[0] === `no:${type}`) return tokens[0]

  return buildRawBlockString(
    {
      id: 0,
      type: BlockType.Filter,
      key: {value: type},
      provider: createParserProvider(type, {exclusive: true}),
      operator: FilterOperator.Is,
      value: {raw: tokens.join(groupingSupportConfig.valueDelimiter), values: tokens.map(value => ({value}))},
    },
    groupingSupportConfig,
  )
}

function joinQueryParts(...parts: string[]): string {
  return parts.reduce((query, part) => {
    if (!query || !part) return query + part

    const needsBoundary = !/\s$/.test(query) && !/^\s/.test(part) && !query.endsWith('(') && !part.startsWith(')')
    return `${query}${needsBoundary ? ' ' : ''}${part}`
  }, '')
}

function removeBranch(searchQuery: string, branch: BranchOperand): string {
  const {block, container} = branch
  const significant = significantBlocks(container.blocks)
  const operands = significant.filter(candidate => !isBooleanKeyword(candidate))

  if (operands.length === 1 && container.group && container.parent) {
    return removeBranch(searchQuery, {block: container.group, container: container.parent})
  }

  const index = significant.indexOf(block)
  const previous = significant[index - 1]
  const next = significant[index + 1]
  const removesPreviousOperator = isBooleanKeyword(previous)
  const startIndex = removesPreviousOperator ? previous.startIndex : block.startIndex
  const endIndex = removesPreviousOperator ? block.endIndex : isBooleanKeyword(next) ? next.endIndex : block.endIndex
  const exclusiveEndIndex =
    block.type === BlockType.Group && (removesPreviousOperator || !isBooleanKeyword(next)) ? endIndex + 1 : endIndex

  if (container.group) {
    const innerStartIndex = container.group.startIndex + 1
    const innerEndIndex = container.group.endIndex
    const remaining = cleanConsecutiveSpaces(
      joinQueryParts(
        searchQuery.slice(innerStartIndex, startIndex),
        searchQuery.slice(exclusiveEndIndex, innerEndIndex),
      ),
    )
    if (operands.length === 2) {
      return cleanConsecutiveSpaces(
        joinQueryParts(
          searchQuery.slice(0, container.group.startIndex),
          remaining,
          searchQuery.slice(container.group.endIndex + 1),
        ),
      )
    }
    return cleanConsecutiveSpaces(
      joinQueryParts(searchQuery.slice(0, innerStartIndex), remaining, searchQuery.slice(innerEndIndex)),
    )
  }

  return cleanConsecutiveSpaces(joinQueryParts(searchQuery.slice(0, startIndex), searchQuery.slice(exclusiveEndIndex)))
}

function insertBranchBeforeSort(searchQuery: string, blocks: IndexedAnyBlock[], branch: string): string {
  const sortBlock = blocks.find(block => block.type !== BlockType.Group && block.raw.toLowerCase().startsWith('sort:'))
  if (!sortBlock) return cleanConsecutiveSpaces(`${searchQuery} ${branch}`)

  return cleanConsecutiveSpaces(
    `${searchQuery.slice(0, sortBlock.startIndex)}${branch} ${searchQuery.slice(sortBlock.startIndex)}`,
  )
}

/**
 * Replaces one unambiguous filter branch without changing its surrounding boolean expression.
 * Unsafe or ambiguous queries are returned unchanged.
 */
export function replaceFilterBranchInSearchQuery(
  searchQuery: string,
  tokens: string | readonly string[] | undefined,
  type: TokenType,
) {
  if (searchQuery.length > MAX_QUERY_LENGTH || hasUnsafeQuote(searchQuery)) return searchQuery
  const selection = normalizeTokens(tokens, type)
  if (!selection.valid) return searchQuery

  const providers = structuralProviderKeys.map(key => createParserProvider(key, {exclusive: true}))
  const parser = new FilterQueryParser(providers, groupingSupportConfig)
  const parserQuery = maskBalancedFilterValueParentheses(searchQuery, parser)
  if (parserQuery === undefined) return searchQuery
  const parsed = parser.parse(parserQuery)
  const blocks = parsed.blocks
  if (
    !blocks.every(isIndexedAnyBlock) ||
    hasUnsafeStructure(blocks) ||
    containsEmptyFilter(blocks) ||
    containsAmbiguousNoValueBranch(blocks, type)
  ) {
    return searchQuery
  }

  const branches = findFilterBranches(blocks, type)
  if (branches.length > 1 || branches[0]?.block.key.value.startsWith('-')) return searchQuery

  const branch = branches[0]
  if (!branch) {
    if (
      selection.tokens.length === 0 ||
      blocks.some(block => block.type === BlockType.Keyword && block.raw.toUpperCase() === 'OR')
    ) {
      return searchQuery
    }
    const updatedQuery = insertBranchBeforeSort(searchQuery, blocks, buildFilterBranch(type, selection.tokens))
    return updatedQuery.length > MAX_QUERY_LENGTH ? searchQuery : updatedQuery
  }

  if (selection.tokens.length === 0) {
    const updatedQuery = removeBranch(searchQuery, branch)
    return updatedQuery.length > MAX_QUERY_LENGTH ? searchQuery : updatedQuery
  }

  const updatedQuery = cleanConsecutiveSpaces(
    joinQueryParts(
      searchQuery.slice(0, branch.block.startIndex),
      buildFilterBranch(type, selection.tokens),
      searchQuery.slice(branch.block.endIndex),
    ),
  )
  return updatedQuery.length > MAX_QUERY_LENGTH ? searchQuery : updatedQuery
}
